import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/data";
import { useAuth } from "../context/AuthContext";
import { adminPath, alternatePath, clientPath, homePath, isEnglishPath } from "../lib/routes";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const location = useLocation();
  const { user } = useAuth();

  /* =========================================================
     LANGUAGE
  ========================================================= */

  const isEnglish = isEnglishPath(location.pathname);
  const langPrefix = isEnglish ? "/en" : "/fr";
  const homeHref = homePath(isEnglish);
  const otherLangHref = alternatePath(location.pathname);

  /* =========================================================
     LINKS
  ========================================================= */

  const LINKS = [
    {
      to: homeHref,
      label: isEnglish ? "Home" : "Accueil",
    },
    {
      to: `${langPrefix}/services`,
      label: "Services",
    },
    {
      to: `${langPrefix}/portfolio`,
      label: isEnglish ? "Work" : "Réalisations",
    },
    {
      to: isEnglish ? `${langPrefix}/about` : `${langPrefix}/a-propos`,
      label: isEnglish ? "About" : "À propos",
    },
    {
      to: `${langPrefix}/contact`,
      label: "Contact",
    },
  ];

  /* =========================================================
     CLIENT AREA / ADMIN
  ========================================================= */

  const isStaff =
    user &&
    ["ceo", "manager", "employee"].includes(user.role);

  /*
   * FR:
   * /fr/espace-client
   *
   * EN:
   * /en/client-area
   */

  const spaceHref = isStaff
    ? adminPath(isEnglish)
    : clientPath(isEnglish);

  const spaceLabel = isStaff
    ? isEnglish
      ? "Dashboard"
      : "Tableau de bord"
    : user
      ? isEnglish
        ? "My space"
        : "Mon espace"
      : isEnglish
        ? "Client area"
        : "Espace client";

  /* =========================================================
     SCROLL
  ========================================================= */

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    onScroll();

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  /* =========================================================
     CLOSE MOBILE MENU ON ROUTE CHANGE
  ========================================================= */

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {/* =======================================================
          HEADER
      ======================================================= */}

      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-black/60 backdrop-blur-xl border-b border-white/10 py-4"
            : "bg-transparent border-b border-transparent py-6"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between">

          {/* ===================================================
              LOGO
          =================================================== */}

          <Link
            to={homeHref}
            data-testid="nav-logo"
            className="group flex items-center gap-2"
          >
            <span className="font-display font-black text-lg md:text-xl tracking-tighter uppercase">
              Hollow<span className="text-acid">Corp</span>
            </span>
          </Link>

          {/* ===================================================
              DESKTOP NAV
          =================================================== */}

          <nav className="hidden lg:flex items-center gap-10">

              {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono-tech text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-acid ${
                    isActive
                      ? "text-acid"
                      : "text-zinc-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

          </nav>

          {/* ===================================================
              DESKTOP RIGHT
          =================================================== */}

          <div className="hidden lg:flex items-center gap-6">

            {/* Client area / Dashboard */}

            <NavLink
              to={otherLangHref}
              data-testid="nav-lang-switch"
              className="font-mono-tech text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-acid transition-colors"
            >
              {isEnglish ? "FR" : "EN"}
            </NavLink>

            <NavLink
              to={spaceHref}
              data-testid="nav-client-space"
              className={({ isActive }) =>
                `font-mono-tech text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-acid ${
                  isActive
                    ? "text-acid"
                    : "text-zinc-300"
                }`
              }
            >
              {spaceLabel}
            </NavLink>

            {/* Quote */}

            <Link
              to={`${langPrefix}/contact`}
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-mono-tech text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-acid hover:text-black hover:border-acid"
            >
              {isEnglish ? "Quote" : "Devis"}

              <ArrowUpRight size={14} />
            </Link>

          </div>

          {/* ===================================================
              MOBILE MENU BUTTON
          =================================================== */}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-1"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? (
              <X size={26} />
            ) : (
              <Menu size={26} />
            )}
          </button>

        </div>
      </header>

      {/* =======================================================
          MOBILE MENU
      ======================================================= */}

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{
              opacity: 0,
              y: -12,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -12,
            }}
            transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="fixed inset-0 z-[99] bg-void pt-28 px-6 lg:hidden"
          >
            <div className="flex flex-col gap-2">

              {/* =================================================
                  MAIN LINKS
              ================================================= */}

              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  transition={{
                    delay: 0.08 * i,
                  }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-link-${l.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    className="block border-b border-white/10 py-5 font-display font-black text-3xl uppercase tracking-tight text-white"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}

              {/* =================================================
                  CLIENT AREA / ADMIN
              ================================================= */}

              <motion.div
                initial={{
                  opacity: 0,
                  x: -20,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  delay: 0.08 * LINKS.length,
                }}
              >
                <NavLink
                  to={spaceHref}
                  data-testid="mobile-client-space"
                  className="block border-b border-white/10 py-5 font-display font-black text-3xl uppercase tracking-tight text-acid"
                >
                  {spaceLabel}
                </NavLink>
                <NavLink
                  to={otherLangHref}
                  data-testid="mobile-lang-switch"
                  className="block border-b border-white/10 py-5 font-display font-black text-3xl uppercase tracking-tight text-zinc-400"
                >
                  {isEnglish ? "Français" : "English"}
                </NavLink>
              </motion.div>

              {/* =================================================
                  EMAIL
              ================================================= */}

              <a
                href={`mailto:${BRAND.email}`}
                className="mt-8 font-mono-tech text-sm text-acid"
              >
                {BRAND.email}
              </a>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}